"use client";

import type React from "react";
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  SimpleGrid,
  Icon,
  Flex,
  Link as ChakraLink,
  Divider,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
  FaEnvelope,
  FaPhone,
  FaShieldAlt,
  FaDatabase,
  FaHistory,
  FaLock,
  FaUserCheck,
  FaWhatsapp,
} from "react-icons/fa";
import { BiMessageDetail } from "react-icons/bi";
import { MdSecurity, MdVerifiedUser } from "react-icons/md";

const PublicHomepage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box minH="100vh" bg="white">
      {/* Navigation */}
      <Box
        as="nav"
        position="sticky"
        top={0}
        zIndex={10}
        bg="white"
        borderBottom="1px"
        borderColor="gray.200"
        boxShadow="sm"
      >
        <Container maxW="7xl" py={4}>
          <Flex justify="space-between" align="center">
            <HStack spacing={3}>
              <Box
                as="img"
                src={process.env.REACT_APP_MAIN_LOGO_URL}
                alt="LeadiumX"
                h="50px"
              />
            </HStack>
            <Button
              variant="brandGradient"
              size="lg"
              onClick={() => navigate("/signin")}
            >
              Login
            </Button>
          </Flex>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        bgGradient="linear(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
        color="white"
        py={20}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          width="600px"
          height="600px"
          bg="primary.800"
          filter="blur(150px)"
          opacity={0.15}
          pointerEvents="none"
        />
        <Container maxW="6xl" position="relative">
          <VStack spacing={6} textAlign="center">
            <Heading
              as="h1"
              fontSize={{ base: "3xl", md: "5xl", lg: "6xl" }}
              fontWeight="bold"
              lineHeight="1.2"
            >
              AI-Powered Outreach
              <br />
              <Text as="span" color="primary.700">
                Automation Platform
              </Text>
            </Heading>
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              maxW="3xl"
              color="gray.300"
              lineHeight="tall"
            >
              LeadiumX empowers businesses to automate outreach campaigns across
              voice, SMS, email, and WhatsApp channels using intelligent AI
              agents.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Platform Features */}
      <Container maxW="7xl" py={20}>
        <VStack spacing={16}>
          <VStack spacing={4} textAlign="center">
            <Heading as="h2" fontSize={{ base: "2xl", md: "4xl" }}>
              Multi-Channel AI Outreach
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="3xl">
              Engage your customers across multiple channels with intelligent AI
              agents that understand context and deliver personalized
              experiences.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8} w="100%">
            <FeatureCard
              icon={FaPhone}
              title="Voice Agents"
              description="AI-powered voice agents that conduct natural conversations, qualify leads, and schedule appointments automatically."
            />
            <FeatureCard
              icon={BiMessageDetail}
              title="SMS Agents"
              description="Automated SMS conversations that engage customers, answer questions, and drive conversions through text messaging."
            />
            <FeatureCard
              icon={FaEnvelope}
              title="Email Agents"
              description="Intelligent email automation that reads incoming messages and responds contextually to maintain ongoing conversations."
            />
            <FeatureCard
              icon={FaWhatsapp}
              title="WhatsApp Agents"
              description="Conversational AI for WhatsApp Business that handles customer inquiries and provides instant support."
            />
          </SimpleGrid>
        </VStack>
      </Container>

      <Box bg="gray.50" py={20}>
        <Container maxW="7xl">
          <VStack spacing={16}>
            <VStack spacing={4} textAlign="center">
              <Icon as={MdSecurity} boxSize={16} color="primary.800" />
              <Heading as="h2" fontSize={{ base: "2xl", md: "4xl" }}>
                Data Privacy & Security
              </Heading>
              <Text fontSize="lg" color="gray.600" maxW="3xl">
                We take data protection seriously. Here's exactly how we handle
                your information and why we need specific permissions.
              </Text>
            </VStack>

            {/* Gmail Data Usage */}
            <Box
              w="100%"
              bg="white"
              p={8}
              borderRadius="xl"
              boxShadow="md"
              border="2px"
              borderColor="primary.100"
            >
              <VStack spacing={6} align="start">
                <HStack spacing={4}>
                  <Icon as={FaEnvelope} boxSize={8} color="primary.800" />
                  <Heading as="h3" fontSize="2xl">
                    Email Automation
                  </Heading>
                </HStack>

                <Text fontSize="md" color="gray.700" lineHeight="tall">
                  Connect your Gmail account to enable automated email outreach
                  campaigns. Our AI email agents can read incoming messages and
                  send responses on your behalf.
                </Text>

                <VStack spacing={4} align="start" w="100%">
                  <DataUsageItem
                    icon={FaDatabase}
                    title="Reading Incoming Emails"
                    description="Our AI agents read emails sent to your connected account to understand customer inquiries and maintain conversation context. This allows them to provide intelligent, relevant responses based on the conversation history."
                    usage="Email content is analyzed to extract conversation context and identify customer needs. Messages are stored in your secure account database to maintain conversation history for your AI agents."
                  />
                  <DataUsageItem
                    icon={BiMessageDetail}
                    title="Sending Outreach Emails"
                    description="AI agents send automated emails as part of your configured campaigns, including initial outreach, follow-ups, and contextual responses to customer messages."
                    usage="Sent emails are logged in your campaign history for tracking and analytics. All messages are sent from your connected Gmail account to maintain authenticity."
                  />
                </VStack>

                <Box bg="blue.50" p={4} borderRadius="md" w="100%">
                  <HStack spacing={3} align="start">
                    <Icon
                      as={FaShieldAlt}
                      color="blue.600"
                      boxSize={5}
                      mt={1}
                    />
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="semibold" color="blue.900">
                        How We Use Your Data
                      </Text>
                      <Text fontSize="sm" color="blue.800" lineHeight="tall">
                        Your email data is used exclusively to provide
                        conversation context to AI agents for intelligent
                        responses and to display campaign analytics in your
                        dashboard. We do NOT train AI models on your data. Each
                        user can only access their own company's data. You can
                        revoke access anytime through your Google Account
                        settings.
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              </VStack>
            </Box>

            {/* Phone/SMS Data Usage */}
            <Box
              w="100%"
              bg="white"
              p={8}
              borderRadius="xl"
              boxShadow="md"
              border="2px"
              borderColor="primary.100"
            >
              <VStack spacing={6} align="start">
                <HStack spacing={4}>
                  <Icon as={FaPhone} boxSize={8} color="primary.800" />
                  <Heading as="h3" fontSize="2xl">
                    Phone & SMS Automation
                  </Heading>
                </HStack>

                <Text fontSize="md" color="gray.700" lineHeight="tall">
                  Connect phone numbers through Twilio to enable voice and SMS
                  outreach campaigns powered by AI agents.
                </Text>

                <VStack spacing={4} align="start" w="100%">
                  <DataUsageItem
                    icon={FaPhone}
                    title="Voice Call Data"
                    description="Call transcripts, recordings, and metrics (duration, status, outcomes) are captured to track campaign performance and provide insights into customer interactions."
                    usage="Call data is stored in your campaign records to provide conversation context for AI agents and generate analytics reports in your dashboard."
                  />
                  <DataUsageItem
                    icon={BiMessageDetail}
                    title="SMS Conversations"
                    description="All SMS messages between AI agents and customers are logged, including message content, timestamps, and delivery status to maintain conversation continuity."
                    usage="Message history enables AI agents to understand conversation context and allows you to review campaign interactions. Messages are linked to specific campaigns and contacts in your account."
                  />
                  <DataUsageItem
                    icon={FaHistory}
                    title="Cross-Channel History"
                    description="We maintain conversation history across all channels (voice, SMS, email, WhatsApp) to provide seamless customer experiences regardless of communication method."
                    usage="Historical conversation data provides context to AI agents for personalized interactions and comprehensive campaign reporting. Only you can access your company's data."
                  />
                </VStack>

                <Box bg="purple.50" p={4} borderRadius="md" w="100%">
                  <HStack spacing={3} align="start">
                    <Icon
                      as={FaUserCheck}
                      color="purple.600"
                      boxSize={5}
                      mt={1}
                    />
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="semibold" color="purple.900">
                        Privacy & Data Isolation
                      </Text>
                      <Text fontSize="sm" color="purple.800" lineHeight="tall">
                        Communication data is stored to provide AI agents with
                        conversation context and to generate campaign analytics
                        for your dashboard. We do NOT use your data to train AI
                        models. Each account's data is completely isolated - you
                        can only view your own company's information. All
                        campaigns comply with TCPA and CAN-SPAM regulations.
                      </Text>
                    </VStack>
                  </HStack>
                </Box>
              </VStack>
            </Box>

            {/* Data Retention */}
            <Box w="100%" bg="white" p={8} borderRadius="xl" boxShadow="md">
              <VStack spacing={4} align="start">
                <HStack spacing={4}>
                  <Icon as={FaLock} boxSize={8} color="primary.800" />
                  <Heading as="h3" fontSize="2xl">
                    Data Storage & Retention
                  </Heading>
                </HStack>
                <Text fontSize="md" color="gray.700" lineHeight="tall">
                  All communication data (emails, SMS, voice transcripts,
                  WhatsApp messages) is stored in encrypted databases linked to
                  your account. We retain this data to:
                </Text>
                <VStack align="start" spacing={2} pl={6}>
                  <Text color="gray.700">
                    • Maintain conversation history for context in ongoing
                    interactions
                  </Text>
                  <Text color="gray.700">
                    • Provide campaign analytics and performance metrics
                  </Text>
                  <Text color="gray.700">
                    • Comply with legal and regulatory requirements
                  </Text>
                </VStack>
                <Text fontSize="md" color="gray.700" lineHeight="tall">
                  You can request data deletion at any time by contacting our
                  support team. We will delete your data within 30 days of
                  account closure, except where required by law to retain it.
                </Text>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* How It Works */}
      <Container maxW="7xl" py={20}>
        <VStack spacing={16}>
          <VStack spacing={4} textAlign="center">
            <Heading as="h2" fontSize={{ base: "2xl", md: "4xl" }}>
              How LeadiumX Works
            </Heading>
            <Text fontSize="lg" color="gray.600" maxW="3xl">
              Simple setup, powerful automation, transparent data handling.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={12} w="100%">
            <ProcessStep
              number="1"
              title="Connect Your Channels"
              description="Link your Gmail account for email automation, connect phone numbers for voice/SMS, and set up WhatsApp Business integration."
            />
            <ProcessStep
              number="2"
              title="Configure AI Agents"
              description="Create intelligent agents with custom instructions, knowledge bases, and conversation flows for each channel."
            />
            <ProcessStep
              number="3"
              title="Launch Campaigns"
              description="Deploy automated outreach campaigns, monitor real-time performance, and let AI handle customer interactions."
            />
          </SimpleGrid>
        </VStack>
      </Container>

      {/* Trust & Security */}
      {/* <Box bg="primary.50" py={20}>
        <Container maxW="7xl">
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={8}>
            <TrustBadge
              icon={MdVerifiedUser}
              title="Google OAuth Verified"
              description="Verified by Google for secure account access"
            />
            <TrustBadge
              icon={FaShieldAlt}
              title="End-to-End Encryption"
              description="All data encrypted in transit and at rest"
            />
            <TrustBadge
              icon={FaLock}
              title="SOC 2 Compliant"
              description="Industry-standard security practices"
            />
            <TrustBadge
              icon={FaUserCheck}
              title="TCPA/CAN-SPAM Compliant"
              description="Automatic compliance with regulations"
            />
          </SimpleGrid>
        </Container>
      </Box> */}

      {/* Footer */}
      <Box bg="gray.900" color="white" py={12}>
        <Container maxW="7xl">
          <VStack spacing={8}>
            <HStack spacing={8} flexWrap="wrap" justify="center">
              <ChakraLink
                onClick={() => navigate("/privacy-policy")}
                fontSize="sm"
                _hover={{ color: "primary.700" }}
                cursor="pointer"
              >
                Privacy Policy
              </ChakraLink>
              <ChakraLink
                onClick={() => navigate("/terms-of-service")}
                fontSize="sm"
                _hover={{ color: "primary.700" }}
                cursor="pointer"
              >
                Terms of Service
              </ChakraLink>
              <ChakraLink
                href="mailto:Support@LeadiumX.ai"
                fontSize="sm"
                _hover={{ color: "primary.700" }}
              >
                Support@LeadiumX.ai
              </ChakraLink>
              <ChakraLink
                href="tel:+18557212247"
                fontSize="sm"
                _hover={{ color: "primary.700" }}
              >
                (855) 721-2247
              </ChakraLink>
            </HStack>
            <Divider />
            <HStack spacing={3}>
              <Box
                as="img"
                src={process.env.REACT_APP_MAIN_LOGO_URL}
                alt="LeadiumX"
                h="40px"
              />
            </HStack>
            <Text fontSize="sm" color="gray.400" textAlign="center">
              © 2025 LeadiumX. All rights reserved.
            </Text>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

// Feature Card Component
const FeatureCard: React.FC<{
  icon: any;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <Box
    bg="white"
    p={8}
    borderRadius="xl"
    boxShadow="lg"
    border="1px"
    borderColor="gray.100"
    transition="all 0.3s"
    _hover={{
      transform: "translateY(-4px)",
      boxShadow: "2xl",
      borderColor: "primary.200",
    }}
  >
    <VStack spacing={4} align="start">
      <Icon as={icon} boxSize={12} color="primary.800" />
      <Heading as="h3" fontSize="xl">
        {title}
      </Heading>
      <Text color="gray.600" lineHeight="tall">
        {description}
      </Text>
    </VStack>
  </Box>
);

// Data Usage Item Component
const DataUsageItem: React.FC<{
  icon: any;
  title: string;
  description: string;
  usage: string;
}> = ({ icon, title, description, usage }) => (
  <Box w="100%" borderLeft="3px" borderColor="primary.800" pl={6} py={2}>
    <VStack spacing={3} align="start">
      <HStack spacing={3}>
        <Icon as={icon} color="primary.900" boxSize={5} />
        <Text fontWeight="bold" fontSize="lg" color="gray.800">
          {title}
        </Text>
      </HStack>
      <Text color="gray.700" fontSize="md">
        {description}
      </Text>
      <Box bg="gray.50" p={3} borderRadius="md" w="100%">
        <Text fontSize="sm" color="gray.700" fontWeight="medium">
          <Text as="span" fontWeight="bold">
            How we use this data:
          </Text>{" "}
          {usage}
        </Text>
      </Box>
    </VStack>
  </Box>
);

// Process Step Component
const ProcessStep: React.FC<{
  number: string;
  title: string;
  description: string;
}> = ({ number, title, description }) => (
  <VStack spacing={4} align="start">
    <Box
      bg="primary.800"
      color="white"
      w={16}
      h={16}
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      fontSize="2xl"
      fontWeight="bold"
    >
      {number}
    </Box>
    <Heading as="h3" fontSize="xl">
      {title}
    </Heading>
    <Text color="gray.600" lineHeight="tall">
      {description}
    </Text>
  </VStack>
);

// Trust Badge Component
const TrustBadge: React.FC<{
  icon: any;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <VStack spacing={3}>
    <Icon as={icon} boxSize={12} color="primary.900" />
    <Text fontWeight="bold" fontSize="lg" textAlign="center">
      {title}
    </Text>
    <Text fontSize="sm" color="gray.600" textAlign="center">
      {description}
    </Text>
  </VStack>
);

export default PublicHomepage;
